import React from "react";
import { css } from "@emotion/react";
import { getIsoCodeWithIntFallback } from "@swo/shared";
import cn from "classnames";
import { usePageLocaleContext } from "../../../../global/page-context";
import { useStore } from "../../../../store/hooks/use-store";
import { ReactTFC } from "../../../../utils/fp/missing-ts-parts";
import { EasySizeComponent } from "../../EasySizeIntegration/EasySizeComponent";
import { getEsConfiguration } from "../../EasySizeIntegration/get-easysize-product";
import { usePdpTranslations } from "../../PdpTranslationsContext";
import { PDPAction, PDPSwarovskiProductFrontend } from "../state/types";
import { StyledList, StyledListElement } from "./ListView";
import { PlainPicker } from "./PlainPicker";
import { ProductAttributeHeadline } from "./ProductAttributeHeadline";
import { VariantPickerVariationCol } from "./utils";
import { ClothSizeVariantView } from "./variations/ClothSizeVariantView";
import { ColoredVariantView } from "./variations/ColoredVariantView";
import { ImagedVariantView } from "./variations/ImagedVariantView";
import { VariantButtonView } from "./variations/VariantButtonView";

export const RenderVariantPicker: ReactTFC<{
  headline: string;
  disabled: boolean;
  attributes: VariantPickerVariationCol;
  selectedProduct: PDPSwarovskiProductFrontend;
  languageName: string;
  onPDPActionClick: (pdpAction: PDPAction) => void;
}> = props => {
  const { state } = useStore();
  const { locale } = usePageLocaleContext();
  const userId = state.auth?.userData?.id;
  const t = usePdpTranslations();

  switch (props.attributes.tag) {
    case "color-picker": {
      return (
        <>
          <ProductAttributeHeadline disabled={props.disabled} headline={props.headline} />

          <ul
            css={css`
              display: flex;
              gap: 20px;
              margin-bottom: 50px;
            `}
          >
            {props.attributes.values.map(v => {
              return (
                <li
                  key={v.name}
                  className={cn({
                    active: v.state === "selected",
                    disabled: v.state === "disabled",
                    enabled: v.state === "selectable",
                  })}
                >
                  <ColoredVariantView variation={v} action={v.target} />
                </li>
              );
            })}
          </ul>
        </>
      );
    }

    case "image-picker": {
      return (
        <>
          <ProductAttributeHeadline disabled={props.disabled} headline={props.headline} />

          <StyledList className={"gridless one-per-line more-gap two"}>
            {props.attributes.values.map(v => {
              const { name, target, state } = v;

              return (
                <StyledListElement
                  key={name}
                  className={cn(state === "selected" ? "active" : v.state === "disabled" ? "disabled" : "enabled")}
                >
                  <ImagedVariantView variation={v} action={target} />
                </StyledListElement>
              );
            })}
          </StyledList>
        </>
      );
    }

    case "plain-picker": {
      return (
        <PlainPicker
          disabled={props.disabled}
          headline={props.headline}
          attributes={{
            ...props.attributes,
            values: props.attributes.values.map(v => ({ ...v, reactKey: v.name })),
          }}
        />
      );
    }

    case "cloth-size-picker": {
      const { selectedProduct } = props;

      const esConfiguration = getEsConfiguration(state, selectedProduct, props.attributes.values, {
        languageName: props.languageName,
        countryIsoCode: getIsoCodeWithIntFallback(locale.country),
        userId,
      });

      return (
        <>
          <ProductAttributeHeadline disabled={props.disabled} headline={props.headline} />
          <div
            className={cn(
              selectedProduct && state.config.integrations.easySize ? "has-children" : undefined,
              "js-cloth-size-picker",
            )}
            css={css`
              display: flex;
              gap: 15px;
              flex-wrap: wrap;

              margin-bottom: 50px;
              &.has-children {
                margin-bottom: 20px;
              }
            `}
          >
            {props.attributes.values.map(v => (
              <ClothSizeVariantView key={v.size.data} variation={v} onPDPActionClick={props.onPDPActionClick} />
            ))}
          </div>

          {esConfiguration && (
            <EasySizeComponent
              esConfiguration={esConfiguration}
              currentlySelectedSize={props.attributes.values.find(v => v.state === "selected")?.size}
              onSizeSelected={(sizeName: string) => {
                if (props.attributes.tag === "cloth-size-picker") {
                  // Looking up by label, not value, because this is what we send to easysize
                  const size = props.attributes.values.find(s => s.size.label === sizeName);

                  if (size && size.target.tag === "click") {
                    props.onPDPActionClick(size.target.onClick);
                  }
                }
              }}
              easySizeTranslations={{
                yourRecommendedSizeIs: t("easySizeYourRecommendedSizeIs"),
                openModalAgain: t("easySizeOpenModalAgain"),
                checkYourSizeCta: t("easySizeCheckYourSizeCta"),
              }}
            />
          )}
        </>
      );
    }

    case "price-only-picker": {
      return (
        <>
          <ProductAttributeHeadline disabled={props.disabled} headline={props.headline} />
          <StyledList className={"two"}>
            {props.attributes.values.map(v => {
              const { target, state, name } = v;
              const className = cn(state === "selected" ? "active" : state === "disabled" ? "disabled" : "enabled");

              return (
                <StyledListElement
                  key={name}
                  className={className}
                  onClick={target.tag === "click" ? () => props.onPDPActionClick(target.onClick) : undefined}
                >
                  <VariantButtonView
                    name={v.additionalText || ""}
                    isNew={v.isNew}
                    target={v.target}
                    state={v.state}
                    gridLess={true}
                    className={className}
                  />
                </StyledListElement>
              );
            })}
          </StyledList>
        </>
      );
    }

    default: {
      const _guard: never = props.attributes;

      return _guard;
    }
  }
};
